import "./styles.css";

import { loadAssets } from "./assets";
import { PlannerRuntime } from "./planner/runtime";
import { readCube } from "./planner/sight";
import { ReposeSim } from "./runtime/mujoco";
import { BrowserPolicy } from "./runtime/policy";
import { ReposeViewer } from "./runtime/viewer";
import { COLORS, type PlannerView } from "./types";

const COLOR_CSS = ["#e63333", "#f2731a", "#33e633", "#e6e633", "#3333e6", "#cc33a6"];
const PROCESSING_MESSAGES = [
  "Decombulating cube topology",
  "Chromafying latent manifold",
  "Splinifying motion manifold",
  "Oracularizing clip lattice",
  "Repatchulating visual tokens",
  "Propriomorphing control phase",
] as const;
const TURN_LABELS: Record<string, string> = {
  R: "ight",
  L: "eft",
  F: "ront",
  B: "ack",
  U: "p",
  D: "own",
};
const query = new URLSearchParams(window.location.search);
const debugStopAt = Number(query.get("stopAt") ?? Number.NaN);
const plannerProfile = query.get("planner") === "v9_3_web" ? "v9_3_web" : "v7_1";

function requireElement<T extends HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`missing ${selector}`);
  return element;
}

const elements = {
  viewer: requireElement<HTMLDivElement>("#viewer"),
  viewport: requireElement<HTMLDivElement>("#viewport"),
  attention: requireElement<HTMLCanvasElement>("#attention-canvas"),
  colors: requireElement<HTMLDivElement>("#color-buttons"),
  boot: requireElement<HTMLElement>("#boot"),
  start: requireElement<HTMLButtonElement>("#start"),
  progress: requireElement<HTMLElement>("#boot-progress"),
  progressBar: requireElement<HTMLElement>("#boot-progress-bar"),
  error: requireElement<HTMLElement>("#error"),
  pauseOverlay: requireElement<HTMLElement>("#pause-overlay"),
  activity: requireElement<HTMLElement>("#activity"),
  activityText: requireElement<HTMLElement>("#activity-text"),
  solvedToast: requireElement<HTMLElement>("#solved-toast"),
  pause: requireElement<HTMLButtonElement>("#pause"),
  reset: requireElement<HTMLButtonElement>("#reset"),
  cameraReset: requireElement<HTMLButtonElement>("#camera-reset"),
  fullscreen: requireElement<HTMLButtonElement>("#fullscreen"),
  mode: requireElement<HTMLElement>("#mode"),
  sees: requireElement<HTMLElement>("#sees"),
  target: requireElement<HTMLElement>("#target"),
  clip: requireElement<HTMLElement>("#clip"),
  ladder: requireElement<HTMLOListElement>("#turn-ladder"),
  plannerSelect: requireElement<HTMLSelectElement>("#planner-select"),
};

elements.plannerSelect.value = plannerProfile;

let sim: ReposeSim | null = null;
let viewer: ReposeViewer | null = null;
let policy: BrowserPolicy | null = null;
let planner: PlannerRuntime | null = null;
let paused = true;
let stopped = false;
let attention: Float32Array | null = null;
let headPixels: Uint8Array = new Uint8Array(112 * 63 * 4);
let processingTarget: number | null = null;
let activityMessage = -1;
let activityChangedAt = 0;
let solvedToastTimer: number | null = null;

function nextActivityMessage(): string {
  let next = activityMessage;
  while (next === activityMessage) next = Math.floor(Math.random() * PROCESSING_MESSAGES.length);
  activityMessage = next;
  return PROCESSING_MESSAGES[activityMessage] ?? "Processing";
}

function setActivity(state: "ready" | "working" | "solved", copy: string): void {
  elements.activity.dataset.state = state;
  elements.activityText.textContent = copy;
}

function hideSolvedToast(): void {
  if (solvedToastTimer !== null) window.clearTimeout(solvedToastTimer);
  solvedToastTimer = null;
  elements.solvedToast.hidden = true;
}

function beginProcessing(target: number): void {
  hideSolvedToast();
  processingTarget = target;
  activityMessage = -1;
  activityChangedAt = performance.now();
  setActivity("working", nextActivityMessage());
}

function showSolved(): void {
  processingTarget = null;
  setActivity("solved", "Solved");
  elements.solvedToast.hidden = false;
  solvedToastTimer = window.setTimeout(() => {
    elements.solvedToast.hidden = true;
    solvedToastTimer = null;
    if (elements.activity.dataset.state === "solved") {
      setActivity("ready", "Ready");
    }
  }, 3200);
}

function updateActivity(state: PlannerView): void {
  if (processingTarget === null) return;
  if (state.stagedTarget === null && state.targetColor === processingTarget && state.solved) {
    showSolved();
    return;
  }
  if (performance.now() - activityChangedAt < 850) return;
  activityChangedAt = performance.now();
  setActivity("working", nextActivityMessage());
}

function setPaused(next: boolean): void {
  paused = next;
  elements.pause.textContent = paused ? "▶" : "Ⅱ";
  elements.pause.title = paused ? "Play" : "Pause";
  elements.pause.setAttribute("aria-label", paused ? "Play simulation" : "Pause simulation");
  elements.pauseOverlay.hidden = !paused || planner === null;
}

function updateDiagnostics(action?: Float32Array): void {
  if (!sim) return;
  const rootHeight = Number(sim.rootPose().pos[2]);
  elements.viewport.dataset.rootHeight = rootHeight.toFixed(4);
  const previousMin = Number(elements.viewport.dataset.minRootHeight ?? rootHeight);
  elements.viewport.dataset.minRootHeight = Math.min(previousMin, rootHeight).toFixed(4);
  elements.viewport.dataset.simTime = Number(sim.data.time).toFixed(3);
  if (action) {
    let maxAction = 0;
    for (const value of action) maxAction = Math.max(maxAction, Math.abs(value));
    elements.viewport.dataset.maxAction = maxAction.toFixed(4);
  }
}

for (const [index, name] of COLORS.entries()) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "color-button";
  button.textContent = name;
  button.style.setProperty("--chip", COLOR_CSS[index] ?? "#777");
  button.dataset.color = String(index);
  button.setAttribute("aria-pressed", String(index === 4));
  button.addEventListener("click", () => {
    if (!planner) return;
    planner.requestTarget(index);
    beginProcessing(index);
    setPaused(false);
    updateStatus();
  });
  elements.colors.append(button);
}

function setProgress(fraction: number, _detail: string): void {
  elements.progress.hidden = false;
  elements.progressBar.style.width = `${Math.round(fraction * 100)}%`;
}

function showError(error: unknown): void {
  console.error(error);
  stopped = true;
  elements.boot.setAttribute("aria-busy", "false");
  elements.error.hidden = false;
  elements.error.textContent = error instanceof Error ? error.message : String(error);
  elements.boot.hidden = true;
}

function updateStatus(): void {
  if (!planner) return;
  const state = planner.view();
  elements.mode.textContent = state.mode;
  elements.sees.textContent = state.seenColor >= 0 ? (COLORS[state.seenColor] ?? "—") : "—";
  elements.target.textContent = COLORS[state.targetColor] ?? "—";
  elements.clip.textContent = state.clipLabel;
  updateLadder(state);
  updateActivity(state);
  for (const button of elements.colors.querySelectorAll<HTMLButtonElement>("button")) {
    const color = Number(button.dataset.color);
    button.setAttribute("aria-pressed", String(color === state.targetColor));
    if (color === state.stagedTarget) button.dataset.staged = "true";
    else delete button.dataset.staged;
  }
}

function updateLadder(state: PlannerView): void {
  if (elements.ladder.dataset.pattern !== state.ladderPattern) {
    elements.ladder.replaceChildren(...Array.from(state.ladderPattern, (turn, index) => {
      const item = document.createElement("li");
      item.className = "turn-step";
      item.dataset.rung = String(index);
      item.innerHTML = `<strong>${turn}</strong><span>${TURN_LABELS[turn] ?? turn}</span>`;
      item.setAttribute("aria-label", `${turn}${TURN_LABELS[turn] ?? ""}`);
      return item;
    }));
    elements.ladder.dataset.pattern = state.ladderPattern;
  }
  const active = state.ladderPattern.length > 0
    ? state.ladderRung % state.ladderPattern.length
    : -1;
  for (const item of elements.ladder.querySelectorAll<HTMLElement>(".turn-step")) {
    const current = Number(item.dataset.rung) === active;
    item.classList.toggle("is-current", current);
    if (current) item.setAttribute("aria-current", "step");
    else item.removeAttribute("aria-current");
  }
}

async function start(): Promise<void> {
  elements.start.disabled = true;
  elements.start.textContent = "Loading…";
  elements.boot.setAttribute("aria-busy", "true");
  try {
    const assets = await loadAssets(setProgress, plannerProfile);
    sim = await ReposeSim.create(assets.bundle, assets.policy, setProgress);
    viewer = new ReposeViewer(sim, elements.viewer, elements.attention);
    headPixels = viewer.render(null, 0);
    policy = await BrowserPolicy.create(assets.encoder, assets.policy, setProgress);
    planner = new PlannerRuntime(assets.planner, assets.table, assets.references, assets.policy);
    const root = sim.rootPose();
    planner.reset(root.quat);
    policy.reset(sim);
    updateDiagnostics();
    setProgress(1, "Ready");
    elements.boot.setAttribute("aria-busy", "false");
    elements.boot.hidden = true;
    setPaused(true);
    setActivity("ready", "Ready");
    updateStatus();
    requestAnimationFrame(renderLoop);
    void controlLoop();
  } catch (error) {
    showError(error);
  }
}

function renderLoop(): void {
  if (stopped || !viewer) return;
  headPixels = viewer.render(attention, 0);
  requestAnimationFrame(renderLoop);
}

async function controlLoop(): Promise<void> {
  while (!stopped) {
    const started = performance.now();
    if (!paused && sim && policy && planner) {
      try {
        const root = sim.rootPose();
        const cube = sim.cubePose();
        const seen = readCube(root.pos, root.quat, cube.pos, cube.quat);
        planner.update(
          seen,
          policy.cameraOmega(sim),
          root.pos,
          root.quat,
          cube.pos,
          sim.jointPosition(),
        );
        const output = await policy.infer(sim, headPixels, planner.horizon(), planner.view().targetColor);
        attention = output.attention;
        sim.applyAction(output.action);
        for (let step = 0; step < sim.policy.control.decimation; step += 1) sim.stepPhysics();
        planner.advance();
        updateDiagnostics(output.action);
        updateStatus();
        if (Number.isFinite(debugStopAt) && Number(sim.data.time) >= debugStopAt) {
          setPaused(true);
        }
      } catch (error) {
        showError(error);
        return;
      }
    }
    const wait = Math.max(0, 20 - (performance.now() - started));
    await new Promise((resolve) => window.setTimeout(resolve, wait));
  }
}

elements.start.addEventListener("click", () => { void start(); });
elements.pause.addEventListener("click", () => setPaused(!paused));
elements.reset.addEventListener("click", () => {
  if (!sim || !policy || !planner) return;
  sim.reset();
  const root = sim.rootPose();
  planner.reset(root.quat);
  policy.reset(sim);
  attention = null;
  processingTarget = null;
  hideSolvedToast();
  setPaused(true);
  setActivity("ready", "Ready");
  updateDiagnostics();
  updateStatus();
});
elements.cameraReset.addEventListener("click", () => viewer?.resetCamera());
elements.plannerSelect.addEventListener("change", () => {
  const profile = elements.plannerSelect.value;
  if (profile !== "v7_1" && profile !== "v9_3_web") return;
  elements.plannerSelect.disabled = true;
  setPaused(true);
  const next = new URL(window.location.href);
  if (profile === "v7_1") next.searchParams.delete("planner");
  else next.searchParams.set("planner", profile);
  window.location.assign(next);
});
elements.fullscreen.addEventListener("click", () => {
  if (document.fullscreenElement) void document.exitFullscreen();
  else void elements.viewport.requestFullscreen();
});

window.addEventListener("beforeunload", () => {
  stopped = true;
  viewer?.dispose();
  sim?.dispose();
});
